import { useCallback, useEffect, useState } from 'react';

import { useFeatureWalmartUiLazyQuery } from 'generated/sanity-graphql';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

const FEATURE_LOYALTY_WALMART_SINGLETON_ID = 'walmart-singleton';

export const useLoyaltyWalmart = () => {
  const [error, setError] = useState<Error | undefined>();
  const dispatch = useAppDispatch();
  const loyaltyWalmartUI = useAppSelector(selectors.loyalty.selectLoyaltyWalmartUI);
  const loyaltyWalmartUILoading = useAppSelector(selectors.loyalty.selectLoyaltyWalmartUILoading);

  const [queryFeatureWalmart] = useFeatureWalmartUiLazyQuery({
    variables: { featureWalmartUIId: FEATURE_LOYALTY_WALMART_SINGLETON_ID },
    fetchPolicy: 'cache-and-network',
  });

  const fetchWalmartUIData = useCallback(async () => {
    dispatch(actions.loyalty.setLoyaltyWalmartUILoading(true));
    setError(undefined);

    try {
      const { data } = await queryFeatureWalmart();
      dispatch(actions.loyalty.setLoyaltyWalmartUI(data || null));
    } catch (cause) {
      const walmartError = toError(cause);
      setError(walmartError);
      logger.warn({ error: walmartError, message: 'Failed to load Walmart loyalty configuration' });
    } finally {
      dispatch(actions.loyalty.setLoyaltyWalmartUILoading(false));
    }
  }, [dispatch, queryFeatureWalmart]);

  useEffect(() => {
    if (!loyaltyWalmartUI && !loyaltyWalmartUILoading && !error) {
      fetchWalmartUIData();
    }
  }, [error, fetchWalmartUIData, loyaltyWalmartUI, loyaltyWalmartUILoading]);

  return {
    data: loyaltyWalmartUI?.Walmart,
    loading: loyaltyWalmartUILoading,
    error,
    refetch: fetchWalmartUIData,
  };
};
